import { Injectable } from '@angular/core';
import { firstValueFrom, from, Observable, of, Subject } from 'rxjs';
import { Response } from '@models/index.model';

@Injectable()
export class SigntrustUtilsService {
  // clone and unsynchronize an object
  public cloneObj(src) {
    return JSON.parse(JSON.stringify(src));
  }

  // capitalize the first letter of a word
  public titleCaseWord(value: string): string {
    if (!value) {
      return value;
    }
    return value[0].toUpperCase() + value.substring(1).toLowerCase();
  }

  // decode the string
  public decodedData(value: string): string {
    return decodeURIComponent(window.atob(value));
  }

  // encode a string | number | boolean
  public encodedData(value: string | number | boolean): string {
    return window.btoa(encodeURIComponent(value));
  }

  // stringify a JSON object
  public stringifyData(value: any): string {
    return JSON.stringify(value);
  }

  // parse a string JSON to object
  public parseData(value: string): any {
    return JSON.parse(value);
  }

  public parseUserPreferenceConfig(config: string): any {
    return JSON.parse(this.decodedData(config));
  }

  public stringifyUserPreferenceConfig(json): string {
    return this.encodedData(JSON.stringify(json));
  }

  public mapResponseData<T>(res: Response<any>): T {
    if (!res.data) {
      throw new Error('Response data is empty!');
    }
    return res.data;
  }

  public getSessionStorage(key: string): any {
    const item: string | null = sessionStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  }

  public setSessionStorage(key: string, value: any) {
    if (typeof value !== 'string') {
      value = JSON.stringify(value);
    }
    sessionStorage.setItem(key, value);
  }

  public removeSessionStorageItem(key: string) {
    sessionStorage.removeItem(key);
  }

  public getLocalStorage(key: string): any {
    const item: string | null = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  }

  public setLocalStorage(key: string, value: any) {
    if (typeof value !== 'string') {
      value = JSON.stringify(value);
    }
    localStorage.setItem(key, value);
  }

  public removeLocalStorageItem(key: string) {
    localStorage.removeItem(key);
  }

  public round(value: number, precision?: number): number {
    var multiplier = Math.pow(10, precision || 0);
    return Math.round(value * multiplier) / multiplier;
  }

  public parseText64ToImgURL(value: string, ext: string) {
    return `data:image/${ext.toLowerCase()};base64,${value}`;
  }

  public base64ToFile(name: string, extension: string, base64: string) {
    return new File([this._dataURItoBlob(base64, extension)], name, { type: extension });
  }

  private _dataURItoBlob(dataURI, extension) {
    const byteString = window.atob(dataURI);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const int8Array = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      int8Array[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([int8Array], { type: extension });
    return blob;
  }

  public getFileExtension(filename) {
    return filename.split('.').pop();
  }

  public fileToBase64(file?: File): Observable<string> {
    // console.log('fileToBase64', file);
    if (!file) {
      console.error('fileToBase64: No file provided');
      return of(''); // Devuelve un Observable vacío
    }
    const reader = new FileReader();
    return new Observable<string>(subscriber => {
      reader.onload = () => {
        const base64String = reader.result as string;
        subscriber.next(base64String);
        subscriber.complete();
      };
      reader.onerror = error => {
        console.error('fileToBase64: FileReader error', error);
        subscriber.error(error);
      };
      reader.readAsDataURL(file);
    });
  }

  public validateFileSize(fileSize: number, sizeValidate: number) {
    const bytesToMb: number = 0.00000095367432;
    if (fileSize * bytesToMb >= sizeValidate) {
      return false;
    } else {
      return true;
    }
  }

  public validateFileType(fileType: string, typesValidate: string[]) {
    // TYPE VALIDATE
    let isValidFile = false;
    for (var index in typesValidate) {
      if (fileType === typesValidate[index]) {
        isValidFile = true;
        break;
      }
    }
    return isValidFile;
  }

  public parsePageRange(rangeStr: string, totalPages: number): number[] | null {
    const result: number[] = [];
    const segments = rangeStr.split(',');
    for (let segment of segments) {
      // Validar segmentos inválidos
      if (segment === '' || segment === ',' || segment.startsWith(',') || segment.endsWith(',')) {
        return null;
      }
      if (segment === '-') {
        for (let i = 1; i <= totalPages; i++) {
          result.push(i);
        }
        continue;
      }
      if (segment.endsWith('-')) {
        const start = parseInt(segment.slice(0, -1), 10);
        if (isNaN(start) || start <= 0 || start > totalPages) return null;
        for (let i = start; i <= totalPages; i++) {
          result.push(i);
        }
        continue;
      }
      if (segment.startsWith('-')) {
        const end = parseInt(segment.slice(1), 10);
        if (isNaN(end) || end <= 0 || end > totalPages) return null;
        for (let i = 1; i <= end; i++) {
          result.push(i);
        }
        continue;
      }
      if (segment.includes('-')) {
        const [startStr, endStr] = segment.split('-');
        const start = parseInt(startStr, 10);
        const end = parseInt(endStr, 10);
        if (isNaN(start) || isNaN(end) || start > end || start <= 0 || end > totalPages) {
          return null;
        }
        for (let i = start; i <= end; i++) {
          result.push(i);
        }
        continue;
      }
      const page = parseInt(segment, 10);
      if (isNaN(page) || page <= 0 || page > totalPages) {
        return null;
      }
      result.push(page);
    }
    return Array.from(new Set(result)).sort((a, b) => a - b);
  }

  public getFileImageDimensions(imageFile): any {
    // console.log('getFileImageDimensions: ', imageFile);
    return new Promise((resolve, reject) => {
      // Crear un objeto URL para el archivo
      const url = URL.createObjectURL(imageFile);
      // Crear un elemento de imagen y asignar la URL
      const img = new Image();
      img.src = url;
      // Esperar a que la imagen se cargue
      img.onload = () => {
        // Obtener las dimensiones
        const width = img.width;
        const height = img.height;
        // Limpiar (liberar memoria)
        URL.revokeObjectURL(url);
        // Resolver la promesa con las dimensiones
        resolve({ width, height });
      };
      // Manejar errores
      img.onerror = () => {
        reject(new Error('No se pudo cargar la imagen'));
      };
    });
  }
  public getBase64ImageDimensions(imageBase64): any {
    // console.log('getBase64ImageDimensions: ', imageBase64);
    return new Promise((resolve, reject) => {
      // Crear un elemento de imagen
      const img = new Image();
      // Asignar la cadena base64 como fuente
      img.src = imageBase64;
      // Esperar a que la imagen se cargue
      img.onload = () => {
        // Obtener las dimensiones
        const width = img.width;
        const height = img.height;
        // Resolver la promesa con las dimensiones
        resolve({ width, height });
      };
      // Manejar errores
      img.onerror = () => {
        reject(new Error('No se pudo cargar la imagen'));
      };
    });
  }

  public parsePdfDate(pdfDateStr: string): Date | null {
    if (!pdfDateStr) return null;

    // 1) Quitar el prefijo "D:" si existe.
    if (pdfDateStr.startsWith('D:')) {
      pdfDateStr = pdfDateStr.substring(2);
    }
    // 2) Extraer partes mínimas: año, mes, día, hora, minuto, segundo
    //    En la práctica, pdfDateStr podría tener menos de 14 dígitos si le faltan segundos, etc.
    const year = parseInt(pdfDateStr.substring(0, 4), 10);
    const month = parseInt(pdfDateStr.substring(4, 6), 10) - 1; // En JS, enero = 0
    const day = parseInt(pdfDateStr.substring(6, 8), 10) || 1;  // Si falta algo, poner un default
    const hour = parseInt(pdfDateStr.substring(8, 10), 10) || 0;
    const minute = parseInt(pdfDateStr.substring(10, 12), 10) || 0;
    const second = parseInt(pdfDateStr.substring(12, 14), 10) || 0;
    // 3) Construir la fecha como si fuera UTC (más fácil para luego ajustar offset).
    let date = new Date(Date.UTC(year, month, day, hour, minute, second));
    if (isNaN(date.getTime())) {
      return null; // Si la fecha es inválida
    }
    // 4) Si hay offset horario (+02'00', -05'30', etc.), parsearlo.
    //    Después de los 14 dígitos, pdfDateStr[14] suele ser '+' o '-' o 'Z'.
    const sign = pdfDateStr.substring(14, 15);
    if (sign === '+' || sign === '-') {
      // Ejemplo: +02'00' => offsetHours=2, offsetMinutes=0
      const offsetHours = parseInt(pdfDateStr.substring(15, 17), 10) || 0;
      const offsetMinutes = parseInt(pdfDateStr.substring(18, 20), 10) || 0;
      // Si es '-', lo invertimos
      const totalOffsetMinutes = (sign === '+' ? 1 : -1) * (offsetHours * 60 + offsetMinutes);
      // Ajustar la fecha en minutos UTC (restando el offset)
      date.setUTCMinutes(date.getUTCMinutes() - totalOffsetMinutes);
    }
    return date;
  }
}
